//Libs
import { useEffect, useState, ChangeEvent } from "react";
import { CalendarDate, today, getLocalTimeZone } from "@internationalized/date";
import type { RangeValue } from "@react-types/shared";
import { v4 as uuidv4 } from "uuid";
import {
  getDownloadURL,
  ref,
  StorageError,
  uploadBytesResumable,
} from "firebase/storage";
import { getStorage } from "firebase/storage";
import { Wrapper } from "@googlemaps/react-wrapper";

//Local
import { DbRead } from "../../database";
import { ExistingVendor } from "../../models/vendor";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import VendorListPage from "./VendorListPage";
import * as strings from "../../strings";
import { logger as devLogger } from "../../logging";

export default function VendorListContainer({ siteKey }: { siteKey: string }) {
  const [siteKeyDoc] = useSiteKeyDocStore((state) => [state.siteKeyDoc]);

  const [vendorList, setVendorList] = useState<ExistingVendor[]>([]);
  const [range, setRange] = useState<RangeValue<CalendarDate>>({
    start: today(getLocalTimeZone()).subtract({
      days: siteKeyDoc?.customizations?.defaultInvoicePageDateRangeDays ?? 30,
    }),
    end: today(getLocalTimeZone()),
  });

  // Logo upload states
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileInformation, setFileInformation] = useState(
    strings.UPLOAD_NO_FILE_CHOSEN,
  );
  const [responseToUserError, setResponseToUserError] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const storage = getStorage();
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAP_KEY;

  useEffect(() => {
    function getVendors() {
      if (!siteKeyDoc) return undefined;

      // Subscribe to all vendors
      const unsubscribe = DbRead.vendors.subscribeAll(siteKey, setVendorList);

      return unsubscribe;
    }

    const unsubscribeFn = getVendors();
    return () => unsubscribeFn && unsubscribeFn();
  }, [siteKey, siteKeyDoc]);

  useEffect(() => {
    if (!selectedFile) {
      setPreview(null);
      return undefined;
    }

    const objectURL = URL.createObjectURL(selectedFile);
    setPreview(objectURL);

    // Free memory when component is unmounted
    return () => URL.revokeObjectURL(objectURL);
  }, [selectedFile]);

  /* fn that handles vendor logo preview and errors from uploading files too big or wrong img type */
  function handlePreviewAndErrors(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files && event.target.files.length === 1) {
      const chosenFile = event.target.files[0];

      // If file is larger than 10MB
      if (chosenFile.size >= 10000000) {
        setResponseToUserError(true);
        setFileInformation(strings.UPLOAD_UNDER_10MB);
        return;
      }

      // Accepted file types: png, jpeg, jpg
      if (
        chosenFile.type !== "image/png" &&
        chosenFile.type !== "image/jpeg" &&
        chosenFile.type !== "image/jpg"
      ) {
        setResponseToUserError(true);
        setFileInformation(strings.UPLOAD_ACCEPTED_FILETYPES);
        return;
      }

      // Set file - ready to be sent to submit handler ƒn
      setSelectedFile(chosenFile);

      // Reset the css styles
      if (responseToUserError) setResponseToUserError(false);
    }
  }

  /* fn to save upload vendor logo in the db storage */
  async function handleVendorLogo(selectedFile: File): Promise<string> {
    // Use a random string as the file's name
    const newFileName = `${uuidv4()}.${selectedFile.type.split("/")[1]}`;

    // Upload file to the path starting with 'siteKeys'
    const storageRef = ref(
      storage,
      `siteKeys/${siteKey}/vendors/` + newFileName,
    );

    const uploadTask = uploadBytesResumable(storageRef, selectedFile);

    return new Promise((resolve, reject) => {
      // Listen for state changes, errors, and completion of the upload.
      const unsubscribe = uploadTask.on("state_changed", {
        // the 'next' callback could give progress reports, ie: '79% done'
        next: null,
        error: handleUploadError,
        complete: onUploadComplete,
      });

      // This only gets called if there is an error.
      function handleUploadError(error: StorageError) {
        switch (error.code) {
          case "storage/unauthorized":
            setFileInformation(
              strings.UPLOAD_ERROR_USER_NOT_PERMITTED_TO_UPLOAD_PHOTO,
            );
            break;
          case "storage/quota-exceeded":
            setFileInformation(strings.UPLOAD_ERROR_QUOTA_EXCEEDED);
            break;
          case "storage/unknown":
            setFileInformation(strings.GENERIC_ERROR_MESSAGE);
            break;
          default:
            setFileInformation(strings.UPLOAD_ERROR_UNABLE);
        }

        devLogger.error("Error occurred while uploading vendor logo:", error);
        unsubscribe();
        reject(error);
      }

      // Upload completed successfully.
      async function onUploadComplete() {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          unsubscribe();
          resolve(downloadURL);
        } catch (err) {
          setFileInformation(strings.UPLOAD_ERROR_UNABLE);
          devLogger.error("Error getting download URL:", err);
          unsubscribe();
          reject(err);
        }
      }
    });
  }

  const resetLogoUploadState = () => {
    setSelectedFile(null);
    setPreview(null);
    setResponseToUserError(false);
    setFileInformation(strings.UPLOAD_NO_FILE_CHOSEN);
  };

  return (
    <Wrapper apiKey={apiKey ?? ""} libraries={["places"]}>
      <VendorListPage
        vendorList={vendorList}
        siteKey={siteKey}
        goToViewVendor={() => {}}
        handleVendorLogo={handleVendorLogo}
        handlePreviewAndErrors={handlePreviewAndErrors}
        preview={preview}
        selectedFile={selectedFile}
        fileInformation={fileInformation}
        responseToUserError={responseToUserError}
        resetLogoUploadState={resetLogoUploadState}
      />
    </Wrapper>
  );
}
